import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, MapPin, Phone, Calendar, ChevronRight,
  CheckCircle, Clock, PlayCircle, TrendingUp
} from 'lucide-react';
import { 
  LIFECYCLE_STATUS, 
  LIFECYCLE_STATUS_LABELS, 
  LIFECYCLE_STATUS_COLORS,
  ONBOARDING_STEPS,
  OFFBOARDING_STEPS
} from '../constants';

/**
 * Modern Hotel Lifecycle Card with:
 * - Rounded corners, shadow, hover effects
 * - Progress bar animation
 * - Status badge color coding
 * - Hotel Name, Property Status, Progress, City, Phone
 */
const HotelLifecycleCard = ({ hotel }) => {
  const navigate = useNavigate();
  
  // Map various backend status values to new LIFECYCLE_STATUS
  const getLifecycleStatus = () => {
    const propStatus = hotel.propertyStatus;
    const lcStatus = hotel.lifecycleStatus;
    const offProgress = hotel.offboardingProgress;
    
    // Phase 19.1 — Offboarded (terminal). Detect via lifecycleStatus,
    // propertyStatus, or 100% offboarding progress. Inactive properties
    // (auto-set after final offboarding) also fall here.
    if (
      lcStatus === 'offboarded' ||
      propStatus === 'Offboarded' ||
      propStatus === 'Inactive' ||
      lcStatus === 'inactive' ||
      (typeof offProgress === 'number' && offProgress >= 100 && propStatus !== 'Active')
    ) {
      return LIFECYCLE_STATUS.OFFBOARDED;
    }
    
    // Phase 15.6 fix — offboarding has priority over the legacy
    // finalGoLiveStatus-based "Onboarded" detection.
    if (propStatus === 'Offboarding' || lcStatus === 'offboarding_in_progress' || lcStatus === 'offboarding') {
      return LIFECYCLE_STATUS.OFFBOARDING;
    }
    
    if (hotel.finalGoLiveStatus) {
      return LIFECYCLE_STATUS.ONBOARDED;
    }
    
    if (propStatus === 'Onboarded' || lcStatus === 'onboarded' || propStatus === 'Live' || lcStatus === 'live') {
      return LIFECYCLE_STATUS.ONBOARDED;
    }
    
    return LIFECYCLE_STATUS.ONBOARDING;
  };
  
  const status = getLifecycleStatus();
  const colors = LIFECYCLE_STATUS_COLORS[status] || LIFECYCLE_STATUS_COLORS[LIFECYCLE_STATUS.ONBOARDING];

  const getStatusIcon = () => {
    switch (status) {
      case LIFECYCLE_STATUS.ONBOARDED:
        return <CheckCircle size={16} className="text-green-500" />;
      case LIFECYCLE_STATUS.ONBOARDING:
        return <PlayCircle size={16} className="text-blue-500" />;
      case LIFECYCLE_STATUS.OFFBOARDING:
        return <Clock size={16} className="text-orange-500" />;
      case LIFECYCLE_STATUS.OFFBOARDED:
        return <CheckCircle size={16} className="text-slate-500" />;
      default:
        return <PlayCircle size={16} className="text-blue-500" />;
    }
  };

  const getProgressPercent = () => {
    // Phase 19.1 — Fully OFFBOARDED is a terminal state; render 100% (7/7).
    // The underlying offboardingSteps may not all be persisted as "completed"
    // when the lifecycle was completed via /complete-offboarding (offboarding
    // is then marked completed at the lifecycle level), so we force 100% here
    // instead of recomputing from the granular step map.
    if (status === LIFECYCLE_STATUS.OFFBOARDED) {
      return 100;
    }
    // Phase 15.6 fix — when the property is in OFFBOARDING, progress must
    // track the 7-step offboarding workflow instead of reusing the (now
    // stale-at-100%) onboarding step counts. Previously a freshly-started
    // offboarding would show 100% on the dashboard because the underlying
    // onboardingSteps were all completed from the prior onboarded state.
    if (status === LIFECYCLE_STATUS.OFFBOARDING) {
      const offSteps = hotel.offboardingSteps || {};
      let offCompleted = 0;
      OFFBOARDING_STEPS.forEach((s) => {
        if (offSteps[s.id]?.status === 'completed') offCompleted++;
      });
      const offTotal = OFFBOARDING_STEPS.length;
      return offTotal > 0 ? Math.min(Math.round((offCompleted / offTotal) * 100), 100) : 0;
    }
    // Onboarding / Onboarded → calculate from onboardingSteps (unchanged)
    const steps = hotel.onboardingSteps || {};
    let completed = 0;
    ONBOARDING_STEPS.forEach(step => {
      if (steps[step.id]?.status === 'completed') {
        completed++;
      }
    });
    const totalStepsCount = ONBOARDING_STEPS.length;
    return totalStepsCount > 0 ? Math.min(Math.round((completed / totalStepsCount) * 100), 100) : 0;
  };

  // Calculate completed steps from actual step data
  const getCompletedSteps = () => {
    // Phase 19.1 — fully OFFBOARDED renders 7/7 (terminal state).
    if (status === LIFECYCLE_STATUS.OFFBOARDED) {
      return OFFBOARDING_STEPS.length;
    }
    if (status === LIFECYCLE_STATUS.OFFBOARDING) {
      const offSteps = hotel.offboardingSteps || {};
      let c = 0;
      OFFBOARDING_STEPS.forEach((s) => {
        if (offSteps[s.id]?.status === 'completed') c++;
      });
      return c;
    }
    const steps = hotel.onboardingSteps || {};
    let completed = 0;
    ONBOARDING_STEPS.forEach(step => {
      if (steps[step.id]?.status === 'completed') {
        completed++;
      }
    });
    return completed;
  };

  const progress = getProgressPercent();
  const completedSteps = getCompletedSteps();
  // Phase 15.6 fix — totalSteps for the progress badge must reflect the
  // ACTIVE workflow (7 for offboarding, 6 for onboarding). Previously this
  // was hardcoded to 6 which produced misleading "6/6 Steps" labels on
  // offboarding cards.
  const totalSteps = status === LIFECYCLE_STATUS.OFFBOARDING || status === LIFECYCLE_STATUS.OFFBOARDED
    ? OFFBOARDING_STEPS.length
    : (hotel.totalSteps ?? ONBOARDING_STEPS.length);

  // Get progress bar color based on status
  const getProgressBarColor = () => {
    switch (status) {
      case LIFECYCLE_STATUS.ONBOARDED:
        return 'bg-gradient-to-r from-green-400 to-green-600';
      case LIFECYCLE_STATUS.OFFBOARDING:
        return 'bg-gradient-to-r from-orange-400 to-orange-600';
      case LIFECYCLE_STATUS.OFFBOARDED:
        return 'bg-gradient-to-r from-slate-400 to-slate-600';
      default:
        return 'bg-gradient-to-r from-teal-400 to-teal-600';
    }
  };

  return (
    <div 
      onClick={() => navigate(`/lifecycle/${hotel.id}`)}
      className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-teal-200 transition-all duration-300 cursor-pointer overflow-hidden"
      data-testid={`lifecycle-card-${hotel.id}`}
    >
      {/* Top gradient accent */}
      <div className={`h-1.5 ${
        status === LIFECYCLE_STATUS.ONBOARDED 
          ? 'bg-gradient-to-r from-green-400 to-emerald-500' 
          : status === LIFECYCLE_STATUS.OFFBOARDING
            ? 'bg-gradient-to-r from-orange-400 to-amber-500'
            : 'bg-gradient-to-r from-teal-400 to-cyan-500'
      }`} />

      {/* Content */}
      <div className="p-5">
        {/* Header with Hotel Name and Status */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            {/* Hotel Avatar */}
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-md flex-shrink-0 ${
              status === LIFECYCLE_STATUS.ONBOARDED 
                ? 'bg-gradient-to-br from-green-500 to-emerald-600' 
                : status === LIFECYCLE_STATUS.OFFBOARDING
                  ? 'bg-gradient-to-br from-orange-500 to-amber-600'
                  : 'bg-gradient-to-br from-teal-500 to-cyan-600'
            }`}>
              {hotel.propertyName?.charAt(0) || hotel.hotelName?.charAt(0) || 'H'}
            </div>
            
            {/* Hotel Info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-800 text-base truncate group-hover:text-teal-600 transition-colors">
                {hotel.propertyName || hotel.hotelName || 'Unnamed Property'}
              </h3>
              <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-1">
                <MapPin size={12} className="flex-shrink-0" />
                <span className="truncate">{hotel.city}{hotel.state ? `, ${hotel.state}` : ''}</span>
              </div>
            </div>
          </div>
          
          {/* Arrow Icon */}
          <ChevronRight size={20} className="text-gray-300 group-hover:text-teal-500 group-hover:translate-x-1 transition-all flex-shrink-0" />
        </div>

        {/* Status Badge and Progress */}
        <div className="space-y-3">
          {/* Status Badge */}
          <div className="flex items-center justify-between">
            <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${colors.bg} ${colors.text} text-xs font-semibold`}>
              {getStatusIcon()}
              <span>{LIFECYCLE_STATUS_LABELS[status] || 'Onboarding'}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <TrendingUp size={14} className="text-gray-400" />
              <span className="text-sm font-bold text-gray-700">{progress}%</span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="relative">
            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
              <div 
                className={`h-2 rounded-full transition-all duration-700 ease-out ${getProgressBarColor()}`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Steps Count and Phone */}
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-1.5 text-gray-500">
              <CheckCircle size={12} className="text-green-500" />
              <span className="font-medium">{completedSteps}/{totalSteps} Steps</span>
            </div>
            {hotel.mobile && (
              <div className="flex items-center gap-1.5 text-gray-500">
                <Phone size={12} />
                <span>{hotel.mobile}</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer with Last Updated */}
        <div className="mt-4 pt-3 border-t border-gray-50 flex items-center gap-2 text-xs text-gray-400">
          <Calendar size={12} />
          <span>
            Updated: {hotel.lifecycleUpdatedAt || hotel.updatedAt
              ? new Date(hotel.lifecycleUpdatedAt || hotel.updatedAt).toLocaleDateString('en-IN', { 
                  day: 'numeric', month: 'short', year: 'numeric' 
                })
              : 'Never'
            }
          </span>
        </div>
      </div>
    </div>
  );
};

export default HotelLifecycleCard;
